import { useCallback } from 'react';

import type { AsyncFlagEvaluator } from '@opendoor/auth-fe/forms';
import { globalObservability } from '@opendoor/observability/slim';

import { getFeatureFlag } from '../api/featureFlags';

export const getLocalFeatureFlagOverrides = (featureFlag: string): 'on' | 'off' | null => {
  try {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(`opendoor.cosmos.${featureFlag}`);
    if (stored === 'true') return 'on';
    if (stored === 'false') return 'off';
    return null;
  } catch {
    return null;
  }
};

const variantIsOn = (variant: string): boolean => variant === 'on';
const FLAG_READ_TIMEOUT_MS = 3000;

type FlagReadOutcome = 'resolved' | 'timeout' | 'error';

const reportFlagRead = (report: {
  flag: string;
  outcome: FlagReadOutcome;
  durationMs: number;
}): void => {
  try {
    globalObservability.getDatadogRumClient()?.addAction?.('cosmos_feature_flag_read', report);
  } catch {
    // Telemetry must not change the gate's result.
  }
};

// Callers own reevaluation because identity cookies can change without a React render.
const useFlagEvaluator = ({
  isEnabled,
  timeBounded,
}: {
  isEnabled: (variant: string) => boolean;
  timeBounded: boolean;
}): AsyncFlagEvaluator => {
  return useCallback(
    async (flag: string) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let outcome: FlagReadOutcome = 'resolved';
      const startedAt = performance.now();
      try {
        const localOverride = getLocalFeatureFlagOverrides(flag);
        if (localOverride !== null) {
          return isEnabled(localOverride);
        }
        if (!timeBounded) {
          return isEnabled((await getFeatureFlag({ key: flag })) ?? 'control');
        }

        const controller = new AbortController();
        const read = getFeatureFlag({ key: flag, signal: controller.signal }).then((value) =>
          isEnabled(value ?? 'control'),
        );
        const timeout = new Promise<boolean>((resolve) => {
          timer = setTimeout(() => {
            outcome = 'timeout';
            resolve(false);
            controller.abort();
          }, FLAG_READ_TIMEOUT_MS);
        });
        return await Promise.race([read, timeout]);
      } catch (error) {
        outcome = 'error';
        throw error;
      } finally {
        clearTimeout(timer);
        if (timeBounded) {
          reportFlagRead({ flag, outcome, durationMs: performance.now() - startedAt });
        }
      }
    },
    [isEnabled, timeBounded],
  );
};

export const useFeatureFlagEvaluator = (
  isEnabled: (variant: string) => boolean = variantIsOn,
): AsyncFlagEvaluator => useFlagEvaluator({ isEnabled, timeBounded: false });

export const useTimeBoundedFeatureFlagEvaluator = (
  isEnabled: (variant: string) => boolean = variantIsOn,
): AsyncFlagEvaluator => useFlagEvaluator({ isEnabled, timeBounded: true });
